import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
/** Catalog reference data: categories, brands, units, tags (docs/specs/catalog.md, C-01). */
const name = z.string().trim().min(1).max(100);
const view = { permission: "catalog.view" };
const manage = { permission: "catalog.manage" };
const catalog = { tag: "catalog" };
// Categories ------------------------------------------------------------------------------------------------
export const MAX_CATEGORY_DEPTH = 3;
export const categorySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    parentId: z.uuid().nullable(),
    /** 1 (top level) … 3. */
    depth: z.int(),
    /** VAT rate for products in this category that have none of their own (basis points). */
    defaultTaxRateBp: z.int().nullable(),
    version: z.int(),
});
/** Every category, flat (the Admin builds the tree from `parentId`). */
export const categoryListSchema = z.object({ data: z.array(categorySchema), nextCursor: z.null() });
const taxRate = z.int().min(0).max(10_000);
export const createCategoryRequestSchema = z
    .object({
    name,
    parentId: z.uuid().nullable().default(null),
    defaultTaxRateBp: taxRate.nullable().default(null),
})
    .strict();
/** Rename, move (new parent, or null for top level) or change the default VAT rate. */
export const updateCategoryRequestSchema = z
    .object({
    name: name.optional(),
    parentId: z.uuid().nullable().optional(),
    defaultTaxRateBp: taxRate.nullable().optional(),
    version: z.int().min(1),
})
    .strict()
    .refine((change) => Object.keys(change).some((key) => key !== "version"), "Nothing to change");
// Brands and tags --------------------------------------------------------------------------------------------
export const brandSchema = z.object({ id: z.uuid(), name: z.string(), version: z.int() });
export const brandListSchema = z.object({ data: z.array(brandSchema), nextCursor: z.null() });
export const tagSchema = brandSchema;
export const tagListSchema = brandListSchema;
export const nameRequestSchema = z.object({ name }).strict();
export const renameRequestSchema = z.object({ name, version: z.int().min(1) }).strict();
// Units ------------------------------------------------------------------------------------------------------
export const unitSchema = z.object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    /** Decimals between the selling unit and the stored base unit: kg 3 (stored in grams), pc 0. */
    precision: z.int(),
    /** Seeded for every tenant; cannot be deleted. */
    isSystem: z.boolean(),
    version: z.int(),
});
export const unitListSchema = z.object({ data: z.array(unitSchema), nextCursor: z.null() });
export const createUnitRequestSchema = z
    .object({
    code: z
        .string()
        .trim()
        .regex(/^[a-z][a-z0-9_]{0,15}$/),
    name,
    precision: z.int().min(0).max(3),
})
    .strict();
export const updateUnitRequestSchema = z
    .object({ name: name.optional(), precision: z.int().min(0).max(3).optional(), version: z.int().min(1) })
    .strict()
    .refine((change) => Object.keys(change).some((key) => key !== "version"), "Nothing to change");
// Endpoints --------------------------------------------------------------------------------------------------
function crud(resource, schemas, deleteErrors) {
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    return {
        list: defineEndpoint({
            ...catalog,
            name: `list${cap(resource.plural)}`,
            method: "GET",
            path: resource.path,
            access: view,
            summary: `Every ${resource.singular}`,
            status: 200,
            response: schemas.list,
        }),
        create: defineEndpoint({
            ...catalog,
            name: `create${cap(resource.singular)}`,
            method: "POST",
            path: resource.path,
            access: manage,
            summary: `Add a ${resource.singular}`,
            body: schemas.create,
            status: 201,
            response: schemas.item,
            errors: ["CONFLICT"],
        }),
        update: defineEndpoint({
            ...catalog,
            name: `update${cap(resource.singular)}`,
            method: "PATCH",
            path: `${resource.path}/{id}`,
            access: manage,
            summary: `Change a ${resource.singular} (optimistic version)`,
            params: idParamsSchema,
            body: schemas.update,
            status: 200,
            response: schemas.item,
            errors: ["CONFLICT", "BUSINESS_RULE_VIOLATION"],
        }),
        remove: defineEndpoint({
            ...catalog,
            name: `delete${cap(resource.singular)}`,
            method: "DELETE",
            path: `${resource.path}/{id}`,
            access: manage,
            summary: `Delete a ${resource.singular} that nothing uses`,
            params: idParamsSchema,
            status: 204,
            errors: deleteErrors,
        }),
    };
}
const categories = crud({ plural: "categories", singular: "category", path: "/v1/categories" }, {
    item: categorySchema,
    list: categoryListSchema,
    create: createCategoryRequestSchema,
    update: updateCategoryRequestSchema,
}, ["CATEGORY_IN_USE"]);
export const listCategories = categories.list;
export const createCategory = categories.create;
export const updateCategory = categories.update;
export const deleteCategory = categories.remove;
const brands = crud({ plural: "brands", singular: "brand", path: "/v1/brands" }, { item: brandSchema, list: brandListSchema, create: nameRequestSchema, update: renameRequestSchema }, ["BUSINESS_RULE_VIOLATION"]);
export const listBrands = brands.list;
export const createBrand = brands.create;
export const updateBrand = brands.update;
export const deleteBrand = brands.remove;
const tags = crud({ plural: "tags", singular: "tag", path: "/v1/tags" }, { item: tagSchema, list: tagListSchema, create: nameRequestSchema, update: renameRequestSchema }, []);
export const listTags = tags.list;
export const createTag = tags.create;
export const updateTag = tags.update;
export const deleteTag = tags.remove;
const units = crud({ plural: "units", singular: "unit", path: "/v1/units" }, {
    item: unitSchema,
    list: unitListSchema,
    create: createUnitRequestSchema,
    update: updateUnitRequestSchema,
}, ["BUSINESS_RULE_VIOLATION"]);
export const listUnits = units.list;
export const createUnit = units.create;
export const updateUnit = units.update;
export const deleteUnit = units.remove;
//# sourceMappingURL=catalog-reference.js.map